'use client';
import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { Children, cloneElement, isValidElement, useLayoutEffect, } from 'react';
import { useFieldContext } from './FieldContext';
/**
 * Compound member of <Field>. Marks its single child as the field's labelable
 * control (an input, select, textarea or button, or a component that renders
 * one and forwards these props): the child takes the field id the label's
 * `for` points at, plus aria-describedby, aria-invalid and aria-required.
 * The label names the control through `for`, so no aria-labelledby is added.
 *
 * Wrap the real control, never a layout container:
 *
 * <Field>
 *   <Field.Label>Tags</Field.Label>
 *   <Row gap="sm">
 *     <Field.Control><Input /></Field.Control>
 *     <Button>Add</Button>
 *   </Row>
 * </Field>
 *
 * A group control that names itself from the Field (useFieldLabelling) owns
 * its wiring, so Field.Control steps aside for it.
 */
export function FieldControl({ children }) {
    const ctx = useFieldContext();
    const register = ctx?.registerPart;
    useLayoutEffect(() => register?.('control'), [register]);
    if (!ctx || ctx.groupControl)
        return _jsx(_Fragment, { children: children });
    const child = Children.only(children);
    if (!isValidElement(child))
        return _jsx(_Fragment, { children: children });
    const el = child;
    const describedBy = [ctx.describedBy, el.props['aria-describedby']]
        .filter((id) => id !== undefined && id !== '')
        .join(' ');
    return cloneElement(el, {
        id: ctx.ids.control,
        ...(describedBy !== '' ? { 'aria-describedby': describedBy } : {}),
        ...(ctx.invalid ? { 'aria-invalid': true } : {}),
        ...(ctx.required ? { 'aria-required': true } : {}),
    });
}
//# sourceMappingURL=FieldControl.js.map